import Reveal from '@/components/base/Reveal';
import Logo from '@/components/base/Logo';
import SmartLink from '@/components/base/SmartLink';

type FooterLink = { label: string; href: string; external?: boolean };

const footerLinks: Record<string, FooterLink[]> = {
  Organization: [
    { label: 'Mission', href: '/mission' },
    { label: 'Projects', href: '/projects' },
    { label: 'About Us', href: '/about' },
    { label: 'Contact', href: 'mailto:hello@sapphiremedia.group', external: true },
  ],
  'Get Involved': [
    { label: 'Join Discord', href: 'https://discord.gg/K2zNSVTT9e', external: true },
    { label: 'Volunteer', href: 'mailto:hello@sapphiremedia.group' },
    { label: 'Donate', href: 'https://hcb.hackclub.com/donations/start/sapphire', external: true },
  ],
  'Highlighted Projects': [
    { label: 'Sapphire Radio', href: 'https://sapphire.radio', external: true },
    { label: 'Sapphy Bot', href: '#', external: false },
    { label: 'Open Source Projects', href: 'https://github.com/sapphiremediagroup', external: true },
  ],
  Legal: [
    { label: 'Privacy Policy', href: '/privacy' },
    { label: 'Terms of Service', href: '/terms' },
    { label: 'Code of Conduct', href: '/code-of-conduct' },
  ],
};

const socialLinks: { icon: string; href: string; label: string }[] = [
  // { icon: 'ri-linkedin-line', href: '#', label: 'LinkedIn' },
  // { icon: 'ri-twitter-x-line', href: '#', label: 'X (Twitter)' },
  // { icon: 'ri-instagram-line', href: '#', label: 'Instagram' },
  { icon: 'ri-discord-line', href: 'https://discord.gg/K2zNSVTT9e', label: 'Discord' },
];

export default function Footer() {
  return (
    <footer className="bg-[#050505] border-t border-white/5 px-6 md:px-12 lg:px-16 pt-14 pb-8">
      <div className="max-w-7xl mx-auto">
        {/* Top */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-10 mb-14">
          {/* Brand */}
          <Reveal variant="fadeUp" duration={800} className="col-span-2 lg:col-span-1">
            <div className="text-2xl font-semibold tracking-tight text-white mb-3"><Logo className="h-5 sm:h-6 md:h-7 w-auto text-white shrink-0" /></div>
            <p className="text-xs text-white/50 leading-relaxed max-w-[180px]">
              Built by youth, broadcasting what’s next.
            </p>
            <div className="flex gap-3 mt-5">
              {socialLinks.map(({ icon, href, label }) => (
                <a
                  key={icon}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-white/20 text-white/50 hover:text-white hover:border-white/50 transition-all duration-200 cursor-pointer"
                >
                  <i className={`${icon} text-sm`} />
                </a>
              ))}
            </div>
          </Reveal>

          {/* Links */}
          {Object.entries(footerLinks).map(([category, links], i) => (
            <Reveal key={category} variant="fadeUp" delay={i * 80} duration={700} threshold={0.1}>
              <div className="text-xs font-medium text-white/70 mb-4 tracking-wide">{category}</div>
              <ul className="space-y-2.5">
                {links.map(link => (
                  <li key={link.label}>
                    <SmartLink
                      href={link.href}
                      {...(link.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                      className="text-xs text-white/50 hover:text-white/80 transition-colors duration-200 cursor-pointer"
                    >
                      {link.label}
                    </SmartLink>
                  </li>
                ))}
              </ul>
            </Reveal>
          ))}
        </div>

        {/* Bottom */}
        <Reveal variant="fade" delay={200} duration={800} threshold={0.1}>
          <div className="border-t border-white/5 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-white/45">
              &copy; 2018-{new Date().getFullYear()} Sapphire Media Group. All rights reserved.
            </p>
            <p className="text-xs text-white/35">
              Sapphire Media Group is fiscally sponsored by The Hack Foundation, a U.S. 501(c)3 non-profit organisation.
            </p>
          </div>
        </Reveal>
      </div>
    </footer>
  );
}
